import React, { useState, useMemo } from "react";
import { ChevronRight, MessageCircle, FileText, RotateCcw, Info } from "lucide-react";
import {
  SPECIES,
  CHALLENGES,
  PRODUCTS,
  getProductsBySpeciesAndChallenge,
  getProductsBySpecies,
} from "@/data/products";
import { buildProductWaLink, buildWaLink, WA_TEMPLATES } from "@/lib/whatsapp";
import AiProductAsk from "./AiProductAsk";

export default function ProductFinder() {
  const [step, setStep] = useState(1);
  const [species, setSpecies] = useState(null);
  const [challenge, setChallenge] = useState(null);

  const matches = useMemo(() => {
    if (!species) return [];
    if (!challenge) return getProductsBySpecies(species);
    const refined = getProductsBySpeciesAndChallenge(species, challenge);
    return refined.length ? refined : getProductsBySpecies(species);
  }, [species, challenge]);

  const reset = () => {
    setSpecies(null);
    setChallenge(null);
    setStep(1);
  };

  return (
    <div className="space-y-6" data-testid="product-finder-container">
      <AiProductAsk compact />
      <div className="relative flex items-center gap-3">
        <div className="h-px flex-1 bg-bah-line" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-bah-mute font-semibold">
          Or use the guided finder
        </span>
        <div className="h-px flex-1 bg-bah-line" />
      </div>
      <div className="bg-white border border-bah-line rounded-3xl p-6 md:p-10 shadow-sm" data-testid="product-finder">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8" data-testid="finder-stepper">
        {[
          { n: 1, label: "Species" },
          { n: 2, label: "Challenge" },
          { n: 3, label: "Results" },
        ].map((s, i) => (
          <React.Fragment key={s.n}>
            <div
              className={`flex items-center gap-2 text-xs font-semibold ${
                step >= s.n ? "text-bah-green" : "text-bah-mute"
              }`}
            >
              <span
                className={`h-7 w-7 rounded-full grid place-content-center text-[11px] font-bold border ${
                  step >= s.n
                    ? "bg-bah-green text-white border-bah-green"
                    : "bg-bah-paper text-bah-mute border-bah-line"
                }`}
              >
                {s.n}
              </span>
              <span className="uppercase tracking-[0.18em]">{s.label}</span>
            </div>
            {i < 2 && <div className="flex-1 h-px bg-bah-line" />}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <div data-testid="finder-step-species">
          <h3 className="font-display text-2xl md:text-3xl text-bah-ink">
            Step 1 — Select species
          </h3>
          <p className="mt-2 text-bah-mute text-sm">
            Choose the animal or use-case you're supporting.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {SPECIES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setSpecies(s.id);
                  setStep(2);
                }}
                data-testid={`finder-species-${s.id}`}
                className={`px-4 py-2.5 rounded-full border text-sm font-semibold transition-all ${
                  species === s.id
                    ? "bg-bah-green text-white border-bah-green"
                    : "bg-bah-paper text-bah-ink border-bah-line hover:border-bah-green hover:text-bah-green"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="finder-step-challenge">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-bah-green font-bold">
            <span>Species:</span>
            <span className="px-2 py-0.5 rounded-full bg-bah-green/10">
              {SPECIES.find((x) => x.id === species)?.label}
            </span>
          </div>
          <h3 className="mt-3 font-display text-2xl md:text-3xl text-bah-ink">
            Step 2 — Select challenge
          </h3>
          <p className="mt-2 text-bah-mute text-sm">
            What health challenge are you addressing today?
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {CHALLENGES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setChallenge(c.id);
                  setStep(3);
                }}
                data-testid={`finder-challenge-${c.id}`}
                className={`px-4 py-2.5 rounded-full border text-sm font-semibold transition-all ${
                  challenge === c.id
                    ? "bg-bah-green text-white border-bah-green"
                    : "bg-bah-paper text-bah-ink border-bah-line hover:border-bah-green hover:text-bah-green"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button
              type="button"
              onClick={reset}
              data-testid="finder-restart-step2"
              className="inline-flex items-center gap-2 text-sm font-semibold text-bah-mute hover:text-bah-ink"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Start over
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div data-testid="finder-step-results">
          <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.18em] text-bah-green font-bold">
            <span className="px-2 py-0.5 rounded-full bg-bah-green/10">
              {SPECIES.find((x) => x.id === species)?.label}
            </span>
            {challenge && (
              <>
                <ChevronRight className="h-3.5 w-3.5 text-bah-mute" />
                <span className="px-2 py-0.5 rounded-full bg-bah-orange/10 text-bah-orange">
                  {CHALLENGES.find((x) => x.id === challenge)?.label}
                </span>
              </>
            )}
          </div>
          <h3 className="mt-3 font-display text-2xl md:text-3xl text-bah-ink">
            {matches.length > 0
              ? `${matches.length} relevant Biodeal Animal Health product${matches.length === 1 ? "" : "s"}`
              : "No exact match — talk to the technical team"}
          </h3>

          {matches.length === 0 && (
            <div className="mt-6 bg-bah-paperAlt rounded-xl p-5 border border-bah-line">
              <p className="text-bah-ink">
                We don't have a packaged product matched exactly to that
                species + challenge in the public catalogue. The Biodeal
                Animal Health technical team will help you find the right
                solution.
              </p>
              <a
                href={buildWaLink(WA_TEMPLATES.technical_general + " ")}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp mt-4"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp Technical Team
              </a>
            </div>
          )}

          {matches.length > 0 && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {matches.map((p) => (
                <div
                  key={p.id}
                  className="border border-bah-line rounded-xl p-5 bg-bah-paper"
                  data-testid={`finder-result-${p.id}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-bah-green font-bold">
                    {p.category.replace(/-/g, " ")}
                  </div>
                  <h4 className="mt-1 font-display text-lg text-bah-ink leading-tight">
                    {p.name}
                  </h4>
                  {p.active_ingredient && (
                    <div className="mt-2 text-xs text-bah-mute">
                      {p.active_ingredient}
                    </div>
                  )}
                  {p.use_summary && (
                    <p className="mt-3 text-sm text-bah-ink leading-relaxed line-clamp-3">
                      {p.use_summary}
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <a
                      href={buildProductWaLink(p.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-bah-green hover:text-bah-orange"
                      data-testid={`finder-result-clarify-${p.id}`}
                    >
                      <Info className="h-3.5 w-3.5" /> Request clarification
                    </a>
                    <a
                      href={buildProductWaLink(`Product sheet: ${p.name}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-bah-ink hover:text-bah-orange"
                      data-testid={`finder-result-sheet-${p.id}`}
                    >
                      <FileText className="h-3.5 w-3.5" /> Product sheet
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 rounded-xl bg-bah-green/5 border border-bah-green/20 p-4 text-sm text-bah-ink">
            <strong className="font-semibold">Responsible-use note: </strong>
            This finder is a guidance tool — not medical diagnosis or final
            treatment advice. For professional veterinary guidance, consult a
            qualified veterinarian or contact Biodeal Animal Health technical
            support.
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={buildWaLink(WA_TEMPLATES.technical_general + " ")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
              data-testid="finder-whatsapp"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp Technical Team
            </a>
            <button
              type="button"
              onClick={reset}
              data-testid="finder-restart"
              className="btn-ghost-dark"
            >
              <RotateCcw className="h-4 w-4" /> Start over
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
